import io from "socket.io-client";
import { API_BASE_URL } from "../config/apiConfig";
import { getAuthToken } from "../utils/apiUtils";

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
  }

  connect(token) {
    if (this.socket && this.connected) {
      return this.socket;
    }

    try {
      // Use centralized getAuthToken for consistent token retrieval
      const authToken = token || getAuthToken();

      if (!authToken) {
return null;
      }

      // Validate token is a proper JWT
      if (!authToken.startsWith("eyJ")) {
}

      // Validate API_BASE_URL first
      if (!API_BASE_URL) {
throw new Error("API_BASE_URL not configured");
      }

      // Get base URL without /api suffix (remove trailing /api if present)
      let baseUrl = API_BASE_URL.endsWith("/api")
        ? API_BASE_URL.slice(0, -4)
        : API_BASE_URL;

      // Ensure we have a valid URL with protocol AFTER removing /api
      if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
throw new Error("Invalid API URL configuration - missing protocol");
      }

      const socketUrl = `${baseUrl}/collaborators`;

      // Final validation: make sure URL doesn't have weird patterns
      if (
        socketUrl.includes("https://https") ||
        socketUrl.includes("http://http")
      ) {
throw new Error("Malformed Socket URL - double protocol detected");
      }

      this.socket = io(socketUrl, {
        path: "/socket.io/",
        auth: {
          token: authToken,
        },
        query: {
          token: authToken, // Also try passing in query
        },
        extraHeaders: {
          Authorization: `Bearer ${authToken}`, // Also try as header
        },
        transports: ["polling", "websocket"],
        withCredentials: true,
        timeout: 20000,
        forceNew: true,
      });

      this.setupEventListeners();
      return this.socket;
    } catch (error) {
return null;
    }
  }

  setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      this.connected = true;
      this.reconnectAttempts = 0;

      // Authenticate immediately after connection
      const token = getAuthToken();

      if (token) {
        this.socket.emit("authenticate", token);
      }

      this.emit("socket_connected");
    });

    // Listen for authentication confirmation
    this.socket.on("authenticated", () => {
      this.emit("socket_authenticated");
    });

    // Handle authentication errors
    this.socket.on("authentication_error", (error) => {
this.emit("socket_auth_error", error);
    });

    this.socket.on("disconnect", (reason) => {
      this.connected = false;
      this.emit("socket_disconnected", reason);

      // Attempt to reconnect unless it was intentional
      if (reason === "io server disconnect") {
        this.handleReconnection();
      }
    });

    this.socket.on("connect_error", (error) => {
this.connected = false;
      this.emit("socket_error", error);
      this.handleReconnection();
    });

    // Notification events
    this.socket.on("notification", (data) => {
      this.handleNotification(data);
    });

    // Real-time collaboration events
    this.socket.on("user_status_update", (data) => {
      this.emit("user_status_update", data);
    });

    this.socket.on("user_presence_update", (data) => {
      this.emit("user_presence_update", data);
    });

    this.socket.on("profile_viewer", (data) => {
      this.emit("profile_viewer", data);
    });

    // Typing indicators
    this.socket.on("user_typing_collaboration", (data) => {
      this.emit("user_typing_collaboration", data);
    });

    this.socket.on("user_stopped_typing_collaboration", (data) => {
      this.emit("user_stopped_typing_collaboration", data);
    });

    // Follow events
    this.socket.on("follow_result", (data) => {
      this.emit("follow_result", data);
    });

    // Notification acknowledgments
    this.socket.on("notification_marked_read", (data) => {
      this.emit("notification_marked_read", data);
    });

    // Error handling
    this.socket.on("error", (error) => {
this.emit("socket_error", error);
    });
  }

  handleNotification(data) {
    // The notification system already handles all notification types
    // We just need to emit specific events for components that want to listen
    const { type, data: notificationData } = data;

    // Emit specific events for real-time updates
    switch (type) {
      case "new_follower":
        this.emit("new_follower", notificationData);
        break;
      case "collaboration_request":
        this.emit("collaboration_request", notificationData);
        break;
      case "collaboration_response":
        this.emit("collaboration_response", notificationData);
        break;
      case "new_rating":
        this.emit("new_rating", notificationData);
        break;
      case "rating_response":
        this.emit("rating_response", notificationData);
        break;
      case "skill_endorsement":
        this.emit("skill_endorsement", notificationData);
        break;
      case "endorsement_response":
        this.emit("endorsement_response", notificationData);
        break;
      default:
        this.emit("unknown_notification", data);
    }

    // Note: The main notification system already handles displaying these notifications
    // This service only provides real-time event emission for component updates
  }

  handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay =
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

      setTimeout(() => {
        if (!this.connected && this.socket) {
          this.socket.connect();
        }
      }, delay);
    } else {
      this.emit("max_reconnect_attempts_reached");
    }
  }

  // Event emission and listening
  emit(event, data) {
    const eventListeners = this.listeners.get(event) || [];
    eventListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) { /* ignored */ }
    });
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;

    const eventListeners = this.listeners.get(event);
    const index = eventListeners.indexOf(callback);
    if (index > -1) {
      eventListeners.splice(index, 1);
    }
  }

  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  // Socket actions
  followUser(userId, source = "profile") {
    if (!this.connected) return;
    this.socket.emit("follow_user", { userId, source });
  }

  unfollowUser(userId) {
    if (!this.connected) return;
    this.socket.emit("unfollow_user", { userId });
  }

  joinProfileView(profileUserId) {
    if (!this.connected) return;
    this.socket.emit("join_profile_view", { profileUserId });
  }

  leaveProfileView(profileUserId) {
    if (!this.connected) return;
    this.socket.emit("leave_profile_view", { profileUserId });
  }

  startTypingCollaborationMessage(recipientId) {
    if (!this.connected) return;
    this.socket.emit("typing_collaboration_message", { recipientId });
  }

  stopTypingCollaborationMessage(recipientId) {
    if (!this.connected) return;
    this.socket.emit("stop_typing_collaboration_message", { recipientId });
  }

  updatePresence(status) {
    if (!this.connected) return;

    // Validate status - must be one of: "online", "away", "busy", "invisible"
    const validStatuses = ["online", "away", "busy", "invisible"];
    if (!validStatuses.includes(status)) {
      status = "online";
    }

    this.socket.emit("update_presence", { status });
  }

  markNotificationRead(notificationId) {
    if (!this.connected) return;
    this.socket.emit("mark_notification_read", { notificationId });
  }

  joinCollaborationRoom(collaborationId) {
    if (!this.connected) return;
    this.socket.emit("join_collaboration", { collaborationId });
  }

  leaveCollaborationRoom(collaborationId) {
    if (!this.connected) return;
    this.socket.emit("leave_collaboration", { collaborationId });
  }

  // Utility methods
  isConnected() {
    return this.connected && this.socket?.connected;
  }

  getSocket() {
    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.removeAllListeners();
    }
  }

  // Presence management
  setUserOnline() {
    this.updatePresence("online");
  }

  setUserAway() {
    this.updatePresence("away");
  }

  setUserOffline() {
    this.updatePresence("invisible");
  }

  setUserBusy() {
    this.updatePresence("busy");
  }

  // Activity tracking
  trackActivity(activity) {
    // Map activity to valid status
    const activityMap = {
      browsing: "online",
      working: "busy",
      idle: "away",
      offline: "invisible",
    };
    const status = activityMap[activity] || "online";
    this.updatePresence(status);
  }
}

// Create singleton instance
const socketService = new SocketService();

// Auto-connect when token is available
try {
  const token = getAuthToken();
  if (token) {
    socketService.connect(token);
  }
} catch (error) {
  // Silent fail - user might not be logged in
}

// Handle token changes
const originalSetItem = localStorage.setItem;
localStorage.setItem = function (key, value) {
  originalSetItem.apply(this, arguments);

  // Handle both "token" and "user" keys
  if (key === "token") {
    if (value) {
      socketService.connect(value);
    } else {
      socketService.disconnect();
    }
  } else if (key === "user") {
    try {
      const user = JSON.parse(value);
      if (user?.token) {
        socketService.connect(user.token);
      } else {
        socketService.disconnect();
      }
    } catch (error) {
      // Silent fail - invalid user data
    }
  }
};

// Handle page visibility changes for presence
document.addEventListener("visibilitychange", () => {
  if (socketService.isConnected()) {
    if (document.hidden) {
      socketService.setUserAway();
    } else {
      socketService.setUserOnline();
    }
  }
});

// Handle window focus/blur for presence
window.addEventListener("focus", () => {
  if (socketService.isConnected()) {
    socketService.setUserOnline();
  }
});

window.addEventListener("blur", () => {
  if (socketService.isConnected()) {
    socketService.setUserAway();
  }
});

// Handle page unload
window.addEventListener("beforeunload", () => {
  if (socketService.isConnected()) {
    socketService.setUserOffline();
  }
});

export default socketService;
